// Single integration point for the Vendia Brand OS.
//
// All brand components/assets come from @vendia/brand-design-system. The static
// assets (logos/icons/favicons) live in the brand repo and are served under a
// fixed prefix by the vendiaBrandAssets Vite plugin. This module binds that prefix
// as the default `assetBasePath` so call sites never repeat it.
//
// To rebrand for Vendia Quote or POS later: change PRODUCT + ACCENT below, and
// the appSubtitle in runtime.ts. Everything else comes from the Brand OS unchanged.

import {
  VendiaLogo,
  VendiaProductLogo,
  VendiaProductIcon,
  vendiaTokens,
  vendiaAssetPaths,
  type VendiaProduct
} from '@vendia/brand-design-system';

// The URL prefix under which the Vite plugin serves brand assets (dev + build).
export const BRAND_ASSET_PREFIX = '/vendia-brand-assets';

// This app's product within the Vendia platform. Change here for a different app.
// Product logos/icons exclude "master" (the master logo uses BrandMasterLogo).
type VendiaAppProduct = Exclude<VendiaProduct, 'master'>;
export const PRODUCT: VendiaAppProduct = 'business';

// The accent color token for this product. Business = Royal Purple.
export const ACCENT = vendiaTokens.color.business;
export const MASTER = vendiaTokens.color.master;

export { vendiaTokens, vendiaAssetPaths };

// Convenience: the full URL for a brand asset by its stored path string.
export function brandAssetUrl(assetPath: string): string {
  return `${BRAND_ASSET_PREFIX}/${assetPath.replace(/^\//, '')}`;
}

// Re-export the logo components with the asset prefix bound by default.
export function BrandLogo(props: Omit<Parameters<typeof VendiaProductLogo>[0], 'product' | 'assetBasePath'> & { product?: VendiaAppProduct }) {
  const { product = PRODUCT, ...rest } = props;
  return VendiaProductLogo({ product, assetBasePath: BRAND_ASSET_PREFIX, ...rest });
}

export function BrandMasterLogo(props: Omit<Parameters<typeof VendiaLogo>[0], 'assetBasePath'>) {
  return VendiaLogo({ assetBasePath: BRAND_ASSET_PREFIX, ...props });
}

export function BrandProductIcon(props: Omit<Parameters<typeof VendiaProductIcon>[0], 'product' | 'assetBasePath'> & { product?: VendiaAppProduct }) {
  const { product = PRODUCT, ...rest } = props;
  return VendiaProductIcon({ product, assetBasePath: BRAND_ASSET_PREFIX, ...rest });
}
